import React, { type ReactNode } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import ApprovalActionButton from './ApprovalActionButton';
import ApprovalStatusChip from './ApprovalStatusChip';

export interface EntityCardHeaderCell {
  label: string;
  // Plain values render bold and truncate; `content` replaces the value for custom cells.
  value?: string;
  content?: ReactNode;
}

interface Props {
  approved: boolean;
  cells: Array<EntityCardHeaderCell>;
  countLabel: string;
  expanded: boolean;
  gridTemplateColumns: string;
  statusApproved: boolean;
  statusCount?: number;
  title: string;
  titleAdornment?: ReactNode;
  onApprove: () => void;
  onToggle: () => void;
  onUnapprove: () => void;
}

export default function ChecklistEntityCardHeader({
  approved,
  cells,
  countLabel,
  expanded,
  gridTemplateColumns,
  statusApproved,
  statusCount,
  title,
  titleAdornment,
  onApprove,
  onToggle,
  onUnapprove,
}: Props) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        alignItems: 'center',
        columnGap: 2,
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns,
        p: 2,
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {titleAdornment}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          <ApprovalStatusChip approved={statusApproved} count={statusCount} />
          <Typography variant="body2" color="text.secondary">
            {countLabel}
          </Typography>
        </Stack>
      </Box>

      {cells.map((cell) => (
        // Bottom-aligned so values share a row even when a narrow column wraps its label.
        <Box key={cell.label} sx={{ alignSelf: 'end', minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ display: 'block', letterSpacing: 0.6 }}
          >
            {cell.label.toUpperCase()}
          </Typography>
          {cell.content ?? (
            <Typography variant="body2" fontWeight={600} noWrap title={cell.value}>
              {cell.value}
            </Typography>
          )}
        </Box>
      ))}

      <Box onClick={(event) => event.stopPropagation()} sx={{ alignItems: 'center', display: 'flex' }}>
        <ApprovalActionButton approved={approved} onApprove={onApprove} onUnapprove={onUnapprove} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
